// @flow

import React, { Component } from "react";
import { noop, isEqual, get, flatten } from "lodash";
import type { NullaryFn, BinaryFn } from "@tvg/types/Functional";
import type { RaceTypeCodeEnum } from "@tvg/types/Race";
import formatCurrency from "@tvg/formatter/currency";

import buildColor from "../../_static/ColorPalette";
import ProcessingOverlay from "../../_static/ProcessingOverlay";
import { getListRunnerNumberGroup } from "../../_molecule/BetSummary";
import MessageBox from "../../_molecule/MessageBox";
import BetAmount from "../../_organism/BetAmount";
import { trash20, add } from "../../_static/Icons/icons";
import Icon from "../../_static/Icons";

import {
  BetTicketSection,
  WagersContainer,
  ControlsContainer,
  BetTypeText,
  SelectionsWrapper,
  SelectionsEmpty,
  Cancel,
  Add,
  BetNowButton,
  BetTypeTrashContainer,
  WagersSubmitContainer,
  WarningMessageWrapper,
  TalentPicksWarning,
  BetTicketContainer
} from "./styled-components";
import ScrollWrapper from "../../_molecule/ScrollWrapper";

type Props = {
  betType: string,
  selectedBetAmount: number,
  betAmounts: Array<number>,
  minimumWagerAmount: number,
  betTotal: number,
  selections: Array<Array<string>>,
  favoriteRunners: Array<Array<string>>,
  raceTypeCode: RaceTypeCodeEnum,
  isBetDisabled: boolean,
  onPlaceBet: NullaryFn<mixed>,
  onCancelBet: NullaryFn<mixed>,
  onWarningClose?: NullaryFn<mixed>,
  onDismissError?: NullaryFn<mixed>,
  onChangeBetAmount: BinaryFn<number, boolean, mixed>,
  onCloseCustomAmountModal?: NullaryFn<mixed>,
  isKey: boolean,
  isLeg: boolean,
  isVisible: boolean,
  isProcessing: boolean,
  isBettingError: boolean,
  isSelectionScratched: boolean,
  scratchedSelectionMessage?: string,
  errorBetTitle?: string,
  errorBetMessage?: string,
  hasOtherAmount: boolean,
  raceNumber: string,
  contextBT: "mep" | "talentPicks",
  modalType: "bottom" | "fade",
  selectTalentIdErrorVisible: boolean,
  dismissTalentPicksWarning: NullaryFn<*>,
  isMobile: boolean,
  inIosAppTablet: boolean
};

export default class BetTicket extends Component<Props> {
  static defaultProps = {
    betType: "WN",
    selectedBetAmount: 0,
    betAmounts: [],
    minimumWagerAmount: 0,
    betTotal: 0,
    selections: [],
    favoriteRunners: [],
    raceTypeCode: "T",
    onPlaceBet: noop,
    onCancelBet: noop,
    onChangeBetAmount: noop,
    onDismissError: noop,
    isBetDisabled: false,
    isKey: false,
    isLeg: false,
    isVisible: false,
    isProcessing: false,
    isBettingError: false,
    isSelectionScratched: false,
    hasOtherAmount: false,
    raceNumber: "1",
    modalType: "bottom",
    onWarningClose: noop,
    onCloseCustomAmountModal: noop,
    scratchedSelectionMessage: "",
    errorBetTitle: "",
    errorBetMessage: "",
    contextBT: "mep",
    selectTalentIdErrorVisible: false,
    dismissTalentPicksWarning: noop,
    isMobile: true,
    inIosAppTablet: false
  };

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(nextProps, this.props);
  }

  renderSelections() {
    if (
      this.props.selections.length <= 1 &&
      (!this.props.selections[0] || this.props.selections[0].length === 0)
    ) {
      return (
        <SelectionsEmpty key="empty" data-qa-label="betTicket-runnerGroupEmpty">
          SELECT A RUNNER USING THE
          <Add icon={add} size={16} color={buildColor("green", "600")} />
        </SelectionsEmpty>
      );
    }

    return getListRunnerNumberGroup(
      this.props.selections,
      this.props.raceTypeCode,
      this.props.isKey,
      this.props.isLeg,
      +this.props.raceNumber,
      this.props.favoriteRunners
    );
  }

  render() {
    const hasTalentPicksOnBehalfWarning =
      this.props.contextBT === "talentPicks" &&
      this.props.selectTalentIdErrorVisible;
    const betNowButtonLabel =
      this.props.contextBT === "talentPicks"
        ? `CREATE   ${formatCurrency(this.props.betTotal)}  PICK`
        : `Bet ${formatCurrency(this.props.betTotal)}`;
    return (
      <BetTicketContainer
        isVisible={this.props.isVisible}
        isDualColumn={this.props.modalType === "fade"}
      >
        <TalentPicksWarning>
          {hasTalentPicksOnBehalfWarning && (
            <MessageBox
              type="error"
              title={'"On behalf of field" is empty!'}
              hasIcon
              message={
                'No Talent selected on "On Behalf of field". Please select one on the dropdown above.'
              }
              onClose={this.props.dismissTalentPicksWarning}
            />
          )}
        </TalentPicksWarning>
        <BetTicketSection
          isVisible={this.props.isVisible}
          isMobile={this.props.isMobile}
          data-qa-label="betTicket"
        >
          {this.props.isProcessing && <ProcessingOverlay />}
          {this.props.isSelectionScratched && (
            <WarningMessageWrapper>
              <MessageBox
                type="warning"
                title={this.props.scratchedSelectionMessage}
                onClose={this.props.onWarningClose}
              />
            </WarningMessageWrapper>
          )}
          {this.props.isBettingError && (
            <WarningMessageWrapper>
              <MessageBox
                type="error"
                title={`ERROR - ${get(this.props, "errorBetTitle")}`}
                message={get(this.props, "errorBetMessage")}
                onClose={this.props.onDismissError}
              />
            </WarningMessageWrapper>
          )}
          <BetTypeTrashContainer>
            <BetTypeText data-qa-label="betTicket-betType">
              {this.props.betType}
            </BetTypeText>
            <Cancel
              size="big"
              type="secondary"
              hasRoundedCorners={false}
              onClick={this.props.onCancelBet}
              qaLabel="betTicket-cancelButton"
            >
              <Icon
                icon={trash20}
                size={20}
                color={buildColor("grey", "900")}
              />
            </Cancel>
          </BetTypeTrashContainer>
          <WagersSubmitContainer>
            <WagersContainer>
              <ScrollWrapper>
                <SelectionsWrapper
                  key={flatten(this.props.selections).toString()}
                >
                  {this.renderSelections()}
                </SelectionsWrapper>
              </ScrollWrapper>
            </WagersContainer>
            <ControlsContainer data-qa-label="betTicket-controls">
              <BetAmount
                hasOtherAmount={this.props.hasOtherAmount}
                betAmounts={this.props.betAmounts}
                onChangeBetAmount={this.props.onChangeBetAmount}
                selectedBetAmount={this.props.selectedBetAmount}
                onCloseCustomAmountModal={this.props.onCloseCustomAmountModal}
                minimumAmount={this.props.minimumWagerAmount}
                modalType={this.props.modalType}
                inIosAppTablet={this.props.inIosAppTablet}
              />
              <BetNowButton
                type="bet"
                size="big"
                isStretched
                isDisabled={this.props.isBetDisabled}
                onClick={this.props.onPlaceBet}
                qaLabel="betTicket-betNowButton"
                hasRunnersSelected={this.props.betTotal > 0}
              >
                {this.props.betTotal === 0
                  ? "Select Runners"
                  : betNowButtonLabel}
              </BetNowButton>
            </ControlsContainer>
          </WagersSubmitContainer>
        </BetTicketSection>
      </BetTicketContainer>
    );
  }
}
